(function (global) {
    class EventEmitter {
        #callbacks = {};

        static Event = class {
            type = "";
            target = "";
            data = "";
        };

        addEventListener(eventType, callback) {
            if (typeof callback !== "function") return;
            if (this.#callbacks[eventType] === undefined) {
                this.#callbacks[eventType] = [];
            }

            this.#callbacks[eventType].push(callback);
        }

        removeEventListener(eventType) {
            delete this.#callbacks[eventType];
        }

        removeAllEventListeners(_eventType){
            this.#callbacks = {}
        }

        dispatchEvent(eventType, data) {
            if (this.#callbacks[eventType] === undefined) return;
            
            const event = new EventEmitter.Event();
            event.type = eventType;
            event.target = this;
            event.data = data;

            this.#callbacks[eventType].forEach(callback => {
                callback(event);
            })
        }
    }

    class CirrusReader extends EventEmitter {
        constructor({ clientId, origin, configVersion, config = {}, container, sessionToken }) {
            super();
            if (!clientId) throw new Error('CirrusReader: Missing clientId');
            if (!container) throw new Error('CirrusReader: Missing container element');
            console.info("[CirrusReader] Waiting to configure Cirrus Reader instance.");

            this.performMessageListeningFunction = null;
            this.configVersion = configVersion;
            this.clientId = clientId;
            this.origin = (origin || `https://${clientId}.colibriocirrus.com`) + '/reader';
            this.config = config;
            this.container = container;
            this.iframe = null;
            this.sessionToken = sessionToken;

            this._bindMessageListener();
        }

        initialise( height = '100%' ){
            this.dispatchEvent("readerLoading", {});
            if (this.configVersion) {
                this.origin = `${this.origin}?config=${btoa(this.configVersion)}`;
                this._initIframe(height);
            } else {
                this.configVersion = this._checkConfig().then((result) => {
                    this.origin = `${this.origin}?config=${btoa(result.lastUpdated)}`;
                    this._initIframe(height);
                    return result.lastUpdated;
                });
            }
        }

        async _checkConfig() {
            return await fetch(`${this.origin}/api/config/${btoa(this.clientId)}`)
                .then(res => res.json())
                .then(config => {
                    return {
                        lastUpdated: config.lastUpdated
                    }
                });
        }

        _initIframe(height) {
            this.iframe = document.createElement('iframe');
            this.iframe.title = "Cirrus Reader - Powered by Colibrio";
            this.iframe.src = `${this.origin}`;
            this.iframe.style.flex = '1 1 auto';
            this.iframe.style.display = 'block';
            this.iframe.style.width = '100%';
            this.iframe.style.height = height;
            this.iframe.style.border = '0';
            this.iframe.allow = 'fullscreen';
            this.iframe.setAttribute('data-cirrus-embed', '');
            this.container.style.display = 'flex';
            this.container.style.flexDirection = 'column';
            this.container.setAttribute('tabindex', '0');
            this.container.appendChild(this.iframe);
            this.dispatchEvent("readerLoaded", {});
        }

        _bindMessageListener() {
            this.performMessageListeningFunction = this._performMessageListening.bind(this);
            window.addEventListener('message', this.performMessageListeningFunction);
        }

        _unbindMessageListener() {
            window.removeEventListener('message', this.performMessageListeningFunction);
        }

        _performMessageListening(event) {
            const acceptableOrigin =
                event.origin.includes("localhost") ||
                event.origin.includes("oncirrus.com") ||
                event.origin.includes("oncirr.us") ||
                event.origin.includes("colibriocirrus.com") ||
                event.origin.includes("colibriocirrus.com") ||
                event.origin.includes("colibrio-cirrus.com");
            if (!acceptableOrigin) return;
            const { type, payload } = event.data || {};
            if (!type) return;
            switch (type) {
                case 'cirrus-reader-ready': {
                    this.send('init-cirrus-reader', {
                        configData: this.config,
                        tenantId: this.clientId,
                        session: this.sessionToken
                    });
                    this.dispatchEvent("readerReady", {
                        payload,
                        configData: this.config,
                        tenantId: this.clientId,
                    });
                }
                break;
                case 'cirrus-reader-publication-loaded': {
                    this.dispatchEvent("readerPublicationLoaded", {
                        payload,
                        configData: this.config,
                        tenantId: this.clientId,
                    });
                }
                break;
                case 'cirrus-reader-heartbeat': {
                    this.dispatchEvent("readerHeartbeat", {
                        payload,
                        configData: this.config,
                        tenantId: this.clientId,
                    });
                }
                break;
            }
        }

        _postMessage(type, payload) {
            if (this.iframe && this.iframe.contentWindow) {
                this.iframe.contentWindow.postMessage({ type, payload }, '*');
            }
        }

        send(type, payload) {
            this._postMessage(type, payload);
        }

        destroy() {
            this._unbindMessageListener();
            this.removeAllEventListeners();
            if (this.iframe && this.iframe.parentNode) {
                this.iframe.parentNode.removeChild(this.iframe);
            }
            this.dispatchEvent("readerDestroyed", {});
        }

        getConfig() {
            return this.config;
        }
    }
    // Expose globally
    global.CirrusReader = CirrusReader;
})(window);
